import React, { useCallback, useEffect } from 'react';
import {
  getDocuments,
  getAccessConsentCheck,
  grantAccessConsent
} from '@cp/pages/CaseDetails/components/AllDocumentsV2/actions';
import { ResourceType } from './types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { createDynamicTabs } from '@cp/pages/CaseDetails/components/AllDocumentsV2/utils';
import BankStatement from '@cp/pages/CaseDetails/components/AllDocuments/bankStatement/BankStatementView';
import cx from 'classnames';
import styles from '@cp/pages/CaseDetails/components/AllDocuments/index.module.scss';
import Tabs from '@components/Tabs';
import { DOCUMENTS } from '@cp/pages/CaseDetails/components/AllDocuments/constants';
import TabItem from '../../../../../web-ui-library/src/components/Tabs/TabItem';
import { createQueryParams, readQueryParams } from '@cp/utils/QueryParamsHelper';
import { GenericObject } from '@cp/src/types/CommonConstans';
import { useNavigate, useParams } from 'react-router-dom';
import CircularLoader from '@navi/web-ui/lib/icons/CircularLoaderIcon';
import { createKey } from '@cp/src/utils/CaseDetail.utils';
import { PAUSE_CASE_LH_VISIBILITY_ROLES } from '@cp/src/constants/Common.constants';
import { Roles } from '@cp/src/pages/auth/constants/AuthConstants';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import OperativeHoursModal from './OperativeHoursModal';
import PIIAcknowledgmentModal from './PIIAcknowledgmentModal';
import { AccessConsentStatus } from '@cp/src/reducers/commonSlice';

function AllDocumentsV2() {
  const dispatch = useDispatch();
  const documents = useSelector((state: RootState) => state?.documentSlice?.documents) || {
    documentDetails: {}
  };
  const bankStatementStatus = useSelector(
    (state: RootState) => state?.bankStatementSlice?.bankStatementStatus
  );
  const user = useSelector((state: RootState) => state.common?.userData);
  const accessConsentStatus = useSelector((state: RootState) => state.common?.accessConsentStatus);

  const isBankStatementAccessible = user?.roles?.some((role: string) =>
    [Roles.ROLE_BANK_STATEMENT_ACCESS, Roles.ROLE_CENTRAL_UNIT].includes(role as Roles)
  );
  const isDocumentLoading = useSelector(
    (state: RootState) => state?.documentSlice?.isDocumentsLoading
  );
  const {
    loanId = '',
    customerId = '',
    caseBusinessVertical = '',
    caseReferenceId = ''
  } = useParams();

  const isSnoozed = useSelector(
    (state: RootState) =>
      state.caseDetail.pageData?.[createKey(loanId, customerId)]?.pause?.data?.isPaused
  );

  const hasVisibilityRole = user?.roles?.some(role =>
    PAUSE_CASE_LH_VISIBILITY_ROLES.includes(role)
  );

  const isImpersonation = useSelector(
    (state: RootState) => state?.common?.userData?.userImpersonated
  );

  // Don't show modals if user is impersonating
  const showPIIModal =
    !isImpersonation && accessConsentStatus === AccessConsentStatus.CONSENT_PENDING;
  const hasAcknowledgedPII = accessConsentStatus === AccessConsentStatus.CONSENT_GIVEN;
  const showOperativeHoursModal =
    !isImpersonation && accessConsentStatus === AccessConsentStatus.BLOCKED;

  const tabs = createDynamicTabs(documents || { documentDetails: {} }, isSnoozed) || [];

  const { [DOCUMENTS]: queryParams } = readQueryParams();
  const navigate = useNavigate();

  const updateAndNavigate = (params: GenericObject) => {
    const url = createQueryParams(params);
    navigate(url, { replace: true });
  };

  const handleTabChange = useCallback((selectedSubTab: string) => {
    const updatedParams = {
      [DOCUMENTS]: {
        selectedTab: selectedSubTab
      }
    };
    updateAndNavigate(updatedParams);
  }, []);

  const staticTabs = [
    {
      key: 'bankStatement',
      value: 'Bank Statement',
      component: <BankStatement />
    }
  ];

  const mergedTabs = [...tabs];

  if (isBankStatementAccessible && !(isSnoozed && !hasVisibilityRole)) {
    mergedTabs.push(...staticTabs);
  }

  useEffect(() => {
    const agentId = user?.referenceId;
    if (agentId && caseReferenceId) {
      dispatch(getAccessConsentCheck(agentId, caseReferenceId, ResourceType.DOCUMENTS));
    }
  }, [caseReferenceId]);

  useEffect(() => {
    // When impersonating, fetch documents immediately without consent check
    if (isImpersonation) {
      getDocuments(dispatch, loanId, customerId, caseReferenceId, caseBusinessVertical);
    } else if (accessConsentStatus === AccessConsentStatus.CONSENT_GIVEN) {
      getDocuments(dispatch, loanId, customerId, caseReferenceId, caseBusinessVertical);
    } else if (accessConsentStatus === AccessConsentStatus.CONSENT_PENDING) {
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_PII_ACKNOWLEDGMENT_MODAL_SHOWN, {
        loanId,
        customerId,
        userId: user?.email
      });
    } else if (accessConsentStatus === AccessConsentStatus.BLOCKED) {
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_PII_REVOKED_MODAL_SHOWN, {
        loanId,
        customerId,
        userId: user?.email
      });
    }
  }, [
    accessConsentStatus,
    loanId,
    customerId,
    caseReferenceId,
    caseBusinessVertical,
    isImpersonation
  ]);

  //select first tab on tab load
  useEffect(() => {
    if (mergedTabs.length > 1 && hasAcknowledgedPII) {
      const updatedParams = {
        [DOCUMENTS]: {
          selectedTab: mergedTabs?.[0]?.key
        }
      };
      updateAndNavigate(updatedParams);
    }
  }, [documents, hasAcknowledgedPII]);

  const handlePIIAcknowledgment = (reason: string, otherReason?: string) => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_PII_ACKNOWLEDGMENT_SUBMITTED, {
      loanId,
      customerId,
      reason,
      otherReasonText: otherReason,
      userId: user?.email,
      timestamp: new Date().toISOString()
    });

    dispatch(
      grantAccessConsent({
        agentId: user?.referenceId as string,
        caseReferenceId: caseReferenceId as string,
        resourceType: ResourceType.DOCUMENTS,
        entityId: loanId as string,
        businessVertical: caseBusinessVertical,
        reason,
        comments: otherReason
      })
    );
  };

  const handlePIIModalClose = () => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_PII_ACKNOWLEDGMENT_CANCELLED, {
      loanId,
      customerId,
      userId: user?.email
    });
    navigate(-1);
  };

  const handleOperativeHoursModalClose = () => {
    navigate(-1);
  };

  // Don't show any modals while consent status is being fetched
  if (accessConsentStatus === AccessConsentStatus.UNDEFINED) {
    return (
      <div className="w-full h-[calc(100vh-240px)] flex items-center justify-center">
        <CircularLoader size={'lg'} />
      </div>
    );
  }

  if (showOperativeHoursModal) {
    return (
      <OperativeHoursModal
        visible={showOperativeHoursModal}
        onClose={handleOperativeHoursModalClose}
      />
    );
  }

  if (showPIIModal) {
    return (
      <PIIAcknowledgmentModal
        visible={showPIIModal}
        onClose={handlePIIModalClose}
        onAgree={handlePIIAcknowledgment}
      />
    );
  }

  // Show loader only after PII is acknowledged (or when impersonating)
  if (isDocumentLoading && (hasAcknowledgedPII || isImpersonation)) {
    return (
      <div className="w-full h-[calc(100vh-240px)] flex items-center justify-center">
        <CircularLoader size={'lg'} />
      </div>
    );
  }

  if (mergedTabs.length === 0 && (hasAcknowledgedPII || isImpersonation)) {
    return (
      <div className="w-full h-[calc(100vh-240px)] flex justify-center items-center">
        No documents found
      </div>
    );
  }

  // Only render tabs after PII acknowledgment (or when impersonating)
  if (!hasAcknowledgedPII && !isImpersonation) {
    return null;
  }

  return (
    <div>
      <div
        className={cx(styles.tabContainer, styles.tabContentScroll, {
          [styles.tableContentScrollWithPausedCase]: isSnoozed
        })}
      >
        <Tabs
          selectedTabKey={queryParams?.selectedTab}
          tabsClassName={styles.tabClass}
          orientation="vertical"
          variant="pill"
          onTabChange={event => handleTabChange(event as string)}
          contentClassName={styles.tabContent}
        >
          {mergedTabs.map(tab => (
            <TabItem
              key={tab.key}
              label={tab.value}
              isNew={tab.key === 'bankStatement' && bankStatementStatus}
            >
              {tab.component}
            </TabItem>
          ))}
        </Tabs>
      </div>
    </div>
  );
}

export default AllDocumentsV2;
